import { useState } from 'react'
import { useRegistrarPreco } from '../../../api/queries/creditoDaIa'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro } from '../../../components/Estados'
import { CampoDeSelecao, CampoDeTexto } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'

interface Props {
  /** O modelo que o sistema está de fato usando, para a ajuda do campo. */
  modeloEmUso: string | null
  aoFechar: () => void
}

/**
 * Registrar o preço do token.
 *
 * **Não existe "editar", e o diálogo diz isso.** Preço já converteu chamadas que aconteceram;
 * registrar um novo NÃO reprecifica o que passou — o valor que alguém leu na semana passada não
 * muda sozinho. Um mês em que o preço mudou aparece somando os dois, com os subtotais separados.
 *
 * **O câmbio é digitado, e nunca buscado.** Uma cotação que se move sozinha mudaria a conversão de
 * ontem sem ninguém ter feito nada — o defeito exato que a V31 recusou. E seria a primeira
 * dependência de rede deste sistema para desenhar uma tela.
 *
 * @param props o modelo em uso e o que fazer ao fechar
 * @returns o diálogo
 */
export function DialogoDePreco({ modeloEmUso, aoFechar }: Props) {
  const t = TEXTOS.creditoDaIa.preco
  const [moeda, setMoeda] = useState('USD')
  const [entrada, setEntrada] = useState('')
  const [saida, setSaida] = useState('')
  const [fonte, setFonte] = useState('')
  const [modelo, setModelo] = useState(modeloEmUso ?? '')
  const [cambio, setCambio] = useState('')
  const [cambioFonte, setCambioFonte] = useState('')
  const [cambioEm, setCambioEm] = useState('')
  const [vigencia, setVigencia] = useState(agoraLocal())
  const registrar = useRegistrarPreco()

  const estrangeira = moeda !== 'BRL'
  const faltaCambio = estrangeira && (!cambio || !cambioFonte.trim() || !cambioEm)
  const incompleto = !entrada || !saida || !fonte.trim() || !modelo.trim() || !vigencia || faltaCambio

  const confirmar = () =>
    registrar.mutate(
      {
        moedaDeCobranca: moeda,
        precoEntradaPorMilhao: entrada.replace(',', '.'),
        precoSaidaPorMilhao: saida.replace(',', '.'),
        precoFonte: fonte.trim(),
        modeloDeReferencia: modelo.trim(),
        cambio: estrangeira ? cambio.replace(',', '.') : null,
        cambioFonte: estrangeira ? cambioFonte.trim() : null,
        cambioEm: estrangeira ? cambioEm : null,
        vigenciaInicio: new Date(vigencia).toISOString(),
      },
      { onSuccess: aoFechar },
    )

  return (
    <Dialogo
      titulo={t.titulo}
      descricao={t.ajuda}
      aoFechar={aoFechar}
      rodape={
        <>
          <Botao aparencia="secundario" onClick={aoFechar}>
            Cancelar
          </Botao>
          <Botao onClick={confirmar} disabled={incompleto || registrar.isPending}>
            {t.registrar}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        <CampoDeSelecao rotulo={t.moeda} value={moeda} onChange={(e) => setMoeda(e.target.value)}>
          <option value="USD">Dólar (USD)</option>
          <option value="BRL">Real (BRL)</option>
        </CampoDeSelecao>

        <div className="grid gap-4 sm:grid-cols-2">
          <CampoDeTexto
            rotulo={t.entrada}
            type="number"
            step="0.000001"
            min="0"
            inputMode="decimal"
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
          />
          <CampoDeTexto
            rotulo={t.saida}
            type="number"
            step="0.000001"
            min="0"
            inputMode="decimal"
            value={saida}
            onChange={(e) => setSaida(e.target.value)}
          />
        </div>
        <p className="text-xs text-slate-500">{t.entradaSaidaAjuda}</p>

        {estrangeira && (
          <div className="grid gap-4 sm:grid-cols-3">
            <CampoDeTexto
              rotulo={t.cambio}
              type="number"
              step="0.000001"
              min="0"
              inputMode="decimal"
              value={cambio}
              onChange={(e) => setCambio(e.target.value)}
            />
            <CampoDeTexto rotulo={t.cambioFonte} value={cambioFonte} onChange={(e) => setCambioFonte(e.target.value)} />
            <CampoDeTexto
              rotulo={t.cambioEm}
              type="date"
              value={cambioEm}
              onChange={(e) => setCambioEm(e.target.value)}
            />
          </div>
        )}
        {estrangeira && <p className="text-xs text-slate-500">{t.cambioAjuda}</p>}

        <CampoDeTexto rotulo={t.fonte} ajuda={t.fonteAjuda} value={fonte} onChange={(e) => setFonte(e.target.value)} />
        <CampoDeTexto
          rotulo={t.modelo}
          ajuda={modeloEmUso ? t.modeloAjuda(modeloEmUso) : undefined}
          value={modelo}
          onChange={(e) => setModelo(e.target.value)}
        />
        <CampoDeTexto
          rotulo={t.valeApartirDe}
          ajuda={t.valeApartirDeAjuda}
          type="datetime-local"
          value={vigencia}
          onChange={(e) => setVigencia(e.target.value)}
        />

        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {t.naoReprecifica}
        </p>
        {registrar.isError && <AvisoDeErro erro={registrar.error} />}
      </div>
    </Dialogo>
  )
}

/** `AAAA-MM-DDTHH:MM` no relógio de quem está preenchendo — o `datetime-local` não aceita fuso. */
function agoraLocal(): string {
  const agora = new Date()
  const deslocado = new Date(agora.getTime() - agora.getTimezoneOffset() * 60_000)
  return deslocado.toISOString().slice(0, 16)
}
